/**
 * The days of a visit — `docs/visit-page.md` §15 (Board 11 A the day sheet, B
 * *Add a day*, C *Move a day*) and the office's close-out of a day while the
 * crew app cannot (§27.4, Board 18 D, F).
 *
 * Days are changed with what depends on them in view (do-not-substitute 13).
 * A day is addressed by its index (`/days/:n`), which survives a move (§4.3).
 */
import { z } from 'zod';
import { REACHED_BY } from './values.js';
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'A day, like 2026-10-26.');
const reason = z.string().trim().min(1, 'Say why.').max(2000);
// ---------------------------------------------------------------------------
// Add a day (Board 11 B) — GET /visits/:id/days/add-options, POST /visits/:id/days
// ---------------------------------------------------------------------------
/** `?hours=` and `?splitFromDay=` re-work *Where the hours come from* for what is typed. */
export const addDayOptionsQuerySchema = z.object({
    hours: z.coerce.number().positive().max(24).optional(),
    splitFromDay: z.coerce.number().int().min(1).max(366).optional(),
    crewId: z.string().uuid().optional(),
});
/**
 * `POST /visits/:id/days` — **Add the day**: `{date, crewId, hours, hoursFrom:
 * 'split'|'extra', splitFromDay?, reason?}` (§5.2). Creates the `visits` row
 * through `scheduleVisit` and renumbers the days by date.
 */
export const addDaySchema = z.object({
    date: isoDay,
    crewId: z.string().uuid({ message: 'Pick the crew.' }),
    hours: z.number().positive().max(24),
    hoursFrom: z.enum(['split', 'extra']),
    splitFromDay: z.number().int().min(1).max(366).optional(),
    /** **Why** (optional). */
    reason: z.string().trim().max(2000).default(''),
}).refine((body) => body.hoursFrom !== 'split' || body.splitFromDay !== undefined, { message: 'Pick the day to split the hours off.', path: ['splitFromDay'] });
// ---------------------------------------------------------------------------
// Move a day (Board 11 C) — GET …/days/:n/move-preview?to=, POST …/days/:n/move
// ---------------------------------------------------------------------------
export const movePreviewQuerySchema = z.object({ to: isoDay });
/**
 * `POST /visits/:id/days/:n/move {to, reason}` (§5.2) — **Move the day**: moves
 * the `visits` row (`visit_moves` records it) and does every dependency.
 * `customerText` is the text as *Edit the text* left it — absent sends the
 * prepared one, `null` (cleared) sends nothing.
 */
export const moveDaySchema = z.object({
    to: isoDay,
    /** **Why** (required). */
    reason,
    customerText: z.string().trim().max(1600).nullable().optional(),
});
/**
 * `POST /visits/:id/days/:n/skip {reason}` — **Skip this day**: marks it
 * `skipped`; its hours and materials stay on the visit for another day, or are
 * removed — the popup asks (§15.3).
 */
export const skipDaySchema = z.object({
    reason,
    hoursAndMaterials: z.enum(['keep', 'remove']),
});
/** `POST /visits/:id/days/:n/remove {reason}` — ⋯ *Remove this day*, only for a day not started (§15.1). */
export const removeDaySchema = z.object({ reason });
/** `PUT /visits/:id/days/:n/crew` — moves the day's assignments and tells both crews. */
export const changeCrewSchema = z.object({
    crewId: z.string().uuid(),
});
/**
 * `POST /visits/:id/days/:n/closeout` — **Close out Day 1** (Board 18 D),
 * through `CrewPort.closeOutDay`: `visit_closeouts` (`entered_by`,
 * `reached_by`), `closeout_lines`, `equipment_usage`, then §6.6's posting.
 * Used + wasted + returned must equal loaded (`postCloseout`'s own rule) —
 * the server refuses otherwise; `…/closeout/preview` says so line by line.
 */
export const closeoutSchema = z.object({
    /** **How it reached the office** — *Call · Text · Paper sheet · In person*. */
    reachedBy: z.enum(REACHED_BY),
    lines: z.array(z.object({
        lineId: z.string().uuid(),
        used: z.number().min(0).max(1_000_000_000),
        wasted: z.number().min(0).max(1_000_000_000),
        returned: z.number().min(0).max(1_000_000_000),
    })),
    equipment: z.array(z.object({
        unitId: z.string().uuid().nullable(),
        typeId: z.string().uuid(),
        hours: z.number().min(0).max(24),
    })),
    /** **A word on the day**. */
    note: z.string().trim().max(4000).default(''),
});
//# sourceMappingURL=days.js.map